const LOCAL_STORAGE_KEY = '936DA01F9ABD4d9d80C702AF85C822A8';
const LEGACY_TOKEN_KEY = 'ACCESS_TOKENS';

const ROLE_CLAIM_KEYS = [
  'role', 'roles', 'Role', 'Roles', 'ApplicationRole',
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role',
];

const CLAIM_KEYS = {
  userId: ['UserId', 'userId', 'nameid', 'sub', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
  userGuid: ['UserGuid', 'userGuid'],
  email: ['Email', 'email', 'upn', 'preferred_username', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
  userName: ['UserName', 'userName', 'unique_name', 'name', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
  firstName: ['FirstName', 'firstName', 'given_name'],
  lastName: ['LastName', 'lastName', 'family_name'],
  applicationId: ['ApplicationGuid', 'ApplicationId', 'applicationId'],
};

const getClaimValue = (payload, keys) => {
  for (const key of keys) {
    if (payload?.[key] !== undefined && payload?.[key] !== null && payload?.[key] !== '') {
      return payload[key];
    }
  }
  return null;
};

const getRolesFromPayload = (payload) => {
  const roles = ROLE_CLAIM_KEYS.reduce((all, key) => {
    const raw = payload?.[key];
    if (!raw) return all;
    return all.concat(Array.isArray(raw) ? raw : [raw]);
  }, []);
  return roles.filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i);
};

const getStorageSlot = () => {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
};

const saveToken = (jwt) => {
  const slot = {
    t: jwt.access_token || jwt.token || jwt.accessToken || '',
    r: jwt.refresh_token || jwt.refreshToken || '',
    p: jwt.profile || null,
    m: jwt.metadata || null,
    rm: jwt.rememberMe === true,
  };
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(slot));
  localStorage.removeItem(LEGACY_TOKEN_KEY);
};

const removeToken = () => {
  localStorage.removeItem(LOCAL_STORAGE_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
};

const isTokenExists = () => {
  return (
    localStorage.getItem(LOCAL_STORAGE_KEY) !== null ||
    localStorage.getItem(LEGACY_TOKEN_KEY) !== null
  );
};

const getRawToken = () => {
  const slot = getStorageSlot();
  if (slot?.t) return slot.t;
  return localStorage.getItem(LEGACY_TOKEN_KEY) || '';
};

const getRefreshToken = () => getStorageSlot()?.r || '';

const getRememberMe = () => {
  const slot = getStorageSlot();
  if (!slot) return false;
  // rm === undefined means pre-rememberMe session → treat as allowed
  return slot.rm !== false;
};

const saveProfile = (profile) => {
  const slot = getStorageSlot() || { t: '' };
  slot.p = profile;
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(slot));
};

const getProfile = () => getStorageSlot()?.p || null;

const parseJwt = () => {
  const slot = getStorageSlot();
  if (!slot?.t) return { user: {}, exp: 0, roles: [] };
  try {
    const base64Url = slot.t.split('.')[1];
    if (!base64Url) return { user: {}, exp: 0, roles: [] };
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    const profile = getProfile() || {};
    const fullName = profile.fullName || [profile.firstName, profile.lastName].filter(Boolean).join(' ');
    const firstName = profile.firstName || getClaimValue(payload, CLAIM_KEYS.firstName) || fullName.split(' ')[0] || '';
    const lastName = profile.lastName || getClaimValue(payload, CLAIM_KEYS.lastName) || fullName.split(' ').slice(1).join(' ') || '';
    const email = profile.email || getClaimValue(payload, CLAIM_KEYS.email) || '';
    const roles = getRolesFromPayload(payload);
    const user = {
      UserId: profile.userFriendlyId || getClaimValue(payload, CLAIM_KEYS.userId) || '',
      UserGuid: profile.id || getClaimValue(payload, CLAIM_KEYS.userGuid) || '',
      UserName: getClaimValue(payload, CLAIM_KEYS.userName) || email,
      Email: email,
      FirstName: firstName,
      LastName: lastName,
      Roles: roles,
      ApplicationGuid: getClaimValue(payload, CLAIM_KEYS.applicationId) || '',
    };
    return { ...payload, user, roles: user.Roles, metadata: slot.m || null };
  } catch {
    return { user: {}, exp: 0, roles: [] };
  }
};

const getUser = () => parseJwt().user;
const getUserGuid = () => getUser()?.UserGuid || '';
const getUserId = () => getUser()?.UserId || '';
const getEmail = () => getUser()?.Email || '';
const getFullname = () => {
  const user = getUser();
  return [user.FirstName, user.LastName].filter(Boolean).join(' ').trim() || user.Email || user.UserName || '';
};

const tokenStore = {
  getStorageSlot,
  saveToken,
  removeToken,
  isTokenExists,
  getRawToken,
  getRefreshToken,
  getRememberMe,
  saveProfile,
  getProfile,
  parseJwt,
  getUser,
  getUserGuid,
  getUserId,
  getEmail,
  getFullname,
};

export default tokenStore;
